import { useObserver } from 'mobx-react-lite'
import React, { FC, useCallback } from 'react'
import { Tooltip, makeStyles } from '@material-ui/core'
import { CentricTypography } from '@core/component/CentricTypography'
import QtyNumberInput from '@api/order/components/CellTemplates/components/QtyNumberInput'
import { SwitchImg } from '@resources/images'
import Icon from '@components/std-components/Icons'
import { useTranslation } from 'react-i18next'
import useViewContext from '@modules/order/hooks/use-view-context'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import { stores } from '@stores'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'
import Button from '@components/std-components/Button'
import formatNumberByLocale from '@utils/format-number-by-locale'

interface IAProductQuantityInputFieldProps {
  rowData: any
  IAProductData: any
  disabled: boolean
}
const useStyles = makeStyles(theme => ({
  cellFieldLabel: {
    marginBottom: 4
  },
  initialAllocationField: {
    width: '100%',
    height: 105,
    padding: '10px 10px 10px 10px',
    textAlign: 'center',
    borderBottom: '1px solid #eeeeee',
    backgroundColor: '#fafafa'
  },
  IAProductQtyInputBox: {
    width: 80,
    height: 20,
    marginTop: 0
  },
  initialAllocationInput: {
    marginBottom: 4,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative'
  },
  inputRightIcons: {
    display: 'flex',
    position: 'absolute',
    right: -2
  },
  percentSign: {
    fontSize: 12,
    paddingLeft: 2
  },
  switchBtn: {
    width: 20,
    padding: 0,
    '& .icon': {
      cursor: 'inherit'
    }
  },
  switchIcon: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 3,
    '& img': {
      width: 18,
      height: 18
    }
  }
}))

const IAProductQuantityInputField: FC<IAProductQuantityInputFieldProps> = ({
  rowData,
  IAProductData,
  disabled
}) =>
  useObserver(function useHook() {
    const classes = useStyles()
    const { t } = useTranslation()
    const { uiStore } = useViewContext()
    const {
      updateIAProductQuantity,
      updateProductPercentToggleState,
      dataStore: { productPercentToggleStates }
    } = uiStore as InitialAllocationUIStore<InitialAllocationDataStore>

    const {
      productConfiguration: { featureFlags }
    } = stores

    const isPercentMode = productPercentToggleStates[rowData?.id]?.isPercentMode
    const percentVal = productPercentToggleStates[rowData?.id]?.percentValue
    const { deliveryProductQuantity } = IAProductData || {}
    const { clientOrderProductQuantity = 0, doorOrderProductDoorQuantity } = rowData || {}
    const productQuantity = clientOrderProductQuantity || 0
    const IAProductQuantity = deliveryProductQuantity || 0

    const IAQuantityPercentValue = useCallback(
      value => {
        return productQuantity
          ? Math.round((value / productQuantity) * 100)
          : productQuantity
      },
      [productQuantity]
    )

    const IAQuantity = useCallback(
      value => {
        return productQuantity
          ? Math.round((value / 100) * productQuantity)
          : productQuantity
      },
      [productQuantity]
    )
    const highlightIAQuantity =
      featureFlags?.highlightQuantityDiscrepancies &&
      (doorOrderProductDoorQuantity < IAProductQuantity ||
        productQuantity < IAProductQuantity ||
        (percentVal ?? IAQuantityPercentValue(IAProductQuantity)) > 100)

    const handleQuantityUpdate = useCallback(
      newQuantity => {
        const productQty = isPercentMode ? IAQuantity(newQuantity) : newQuantity
        updateProductPercentToggleState(
          isPercentMode,
          isPercentMode ? newQuantity : IAQuantityPercentValue(newQuantity),
          rowData.id
        )
        updateIAProductQuantity(rowData.id, productQty)
      },
      [
        isPercentMode,
        IAQuantity,
        IAQuantityPercentValue,
        updateIAProductQuantity,
        updateProductPercentToggleState
      ]
    )

    const switchIcon = (
      <div className={classes.inputRightIcons}>
        <Tooltip title={t('order.quantitiesAndBook.switch', 'Switch')}>
          <div>
            <Button
              variant='link'
              size='none'
              className={classes.switchBtn}
              disabled={disabled}
              onClick={() => {
                updateProductPercentToggleState(
                  !isPercentMode,
                  IAQuantityPercentValue(IAProductQuantity),
                  rowData?.id
                )
              }}
            >
              <Icon
                className={classes.switchIcon}
                imageSrc={SwitchImg}
                alt={'swicthIAPercentMode'}
              />
            </Button>
          </div>
        </Tooltip>
        <div></div>
      </div>
    )

    return (
      <div className={classes.initialAllocationField}>
        <div>
          <CentricTypography variant={'body2'} className={classes.cellFieldLabel}>
            {t('common.label.initialAllocation', 'Initial Allocation')}
          </CentricTypography>
        </div>
        <div>
          <div className={classes.initialAllocationInput}>
            <div>
              <QtyNumberInput
                id={rowData.id}
                className={classes.IAProductQtyInputBox}
                onChange={e =>
                  handleQuantityUpdate(getNumberFromLocale(e.target.value || 0))
                }
                value={
                  isPercentMode
                    ? IAQuantityPercentValue(IAProductQuantity) ?? percentVal
                    : IAProductQuantity
                }
                disabled={disabled}
                highlightInput={highlightIAQuantity}
              />
              {isPercentMode ? <span className={classes.percentSign}>%</span> : ''}
            </div>
            {switchIcon}
          </div>
          <div>
            <CentricTypography variant={'subtitle1DarkGrey'}>
              {isPercentMode
                ? formatNumberByLocale(IAProductQuantity)
                : IAQuantityPercentValue(IAProductQuantity)}
              &nbsp;
              {isPercentMode ? '' : '%'}
            </CentricTypography>
          </div>
        </div>
      </div>
    )
  })

export default IAProductQuantityInputField
